import * as fflate from 'fflate';
import { RoiReadModel } from '../readmodels/StructureSetReadModel';

import { Grid } from '../../engine/data/common/Grid';

export interface RoiMemento {
    readonly roi: RoiReadModel;
    readonly compressedContourPixels?: Uint8Array;
    readonly grid?: Grid;
    readonly timestamp: Date;
}

export class ReversibleOperation {
    constructor(
        private readonly state: RoiMemento,
        private readonly description: string
    ) {}

    getDescription(): string {
        return this.description;
    }

    getTimestamp(): Date {
        return this.state.timestamp;
    }

    getState(): RoiMemento {
        return this.state;
    }

    getRoiNumber(): number {
        return this.state.roi.roiNumber;
    }

    hasContourChanges(): boolean {
        return !!this.state.compressedContourPixels;
    }

    getAffectedEntities(): Array<{
        type: 'roi' | 'structureSet' | 'global';
        id: string;
    }> {
        return [
            { type: 'roi', id: `roi-${this.state.roi.roiNumber}` },
            { type: 'structureSet', id: 'current-structure-set' },
        ];
    }
}

/**
 * Factory for creating ROI mementos from RoiReadModel
 */
export class RoiMementoFactory {
    /**
     * Create memento from RoiReadModel
     */
    static fromReadModel(roi: RoiReadModel): RoiMemento {
        return {
            roi, // Already immutable, just store it
            timestamp: new Date(),
        };
    }

    /**
     * Create memento with full contour data.
     * Compression runs in a Web Worker via fflate.compress()
     * to avoid blocking the main thread.
     */
    static async fromReadModelWithContours(
        roi: RoiReadModel,
        contourPixelSlices: Uint8Array[],
        grid: Grid
    ): Promise<RoiMemento> {
        const concatBuffer = new Uint8Array(
            contourPixelSlices.length * contourPixelSlices[0].length
        );

        for (let i = 0; i < contourPixelSlices.length; i++) {
            concatBuffer.set(
                contourPixelSlices[i],
                i * contourPixelSlices[i].length
            );
        }

        const compressedPixels = await new Promise<Uint8Array>(
            (resolve, reject) => {
                fflate.compress(concatBuffer, { level: 1 }, (err, data) => {
                    if (err) {
                        reject(err);
                    } else {
                        resolve(data);
                    }
                });
            }
        );

        return {
            roi,
            compressedContourPixels: compressedPixels,
            grid,
            timestamp: new Date(),
        };
    }
}
